import { useState } from 'react';
import {
  Button,
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogContent,
  DialogActions,
  Field,
  Input,
} from '@fluentui/react-components';
import { WarningRegular } from '@fluentui/react-icons';
import { type UseMutationResult } from '@tanstack/react-query';

export interface LinkXeroInvoiceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  linkInvoiceMutation: UseMutationResult<unknown, Error, string>;
}

export function LinkXeroInvoiceDialog({
  open,
  onOpenChange,
  linkInvoiceMutation,
}: LinkXeroInvoiceDialogProps) {
  const [linkInvoiceId, setLinkInvoiceId] = useState('');

  const handleCancel = () => {
    onOpenChange(false);
    setLinkInvoiceId('');
  };

  const handleLink = () => {
    linkInvoiceMutation.mutate(linkInvoiceId.trim(), {
      onSuccess: () => {
        onOpenChange(false);
        setLinkInvoiceId('');
      },
    });
  };

  return (
    <Dialog open={open} onOpenChange={(_, data) => onOpenChange(data.open)}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>Link Existing Xero Invoice</DialogTitle>
          <DialogContent>
            <p style={{ marginBottom: '16px', color: 'var(--tl-text-secondary)' }}>
              Enter the Xero Invoice ID to link an existing invoice to this tax return.
              You can find this ID in the Xero URL when viewing the invoice.
            </p>
            <Field label="Xero Invoice ID">
              <Input
                value={linkInvoiceId}
                onChange={(_, data) => setLinkInvoiceId(data.value)}
                placeholder="e.g., abc12345-6789-..."
              />
            </Field>
            {linkInvoiceMutation.isError && (
              <div style={{ color: '#d83b01', fontSize: '13px', marginTop: '8px' }}>
                <WarningRegular /> {(linkInvoiceMutation.error as Error)?.message || 'Failed to link invoice'}
              </div>
            )}
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={handleCancel}>
              Cancel
            </Button>
            <Button
              appearance="primary"
              disabled={!linkInvoiceId.trim() || linkInvoiceMutation.isPending}
              onClick={handleLink}
            >
              {linkInvoiceMutation.isPending ? 'Linking...' : 'Link Invoice'}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
